/* eslint-disable @typescript-eslint/no-use-before-define */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useSpeechRecognition(lang: string = 'en-US') {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [isSpeechSupported, setIsSpeechSupported] = useState(true);

  const recognitionRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // ---------------- SPEECH RECOGNITION ----------------
  useEffect(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSpeechSupported(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    recognition.continuous = true; // keep listening
    recognition.interimResults = true; // show partial results
    recognition.lang = lang;

    recognition.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        }
      }
      if (finalTranscript) setCurrentTranscript(finalTranscript);
    };

    recognition.onerror = (event) => {
      // eslint-disable-next-line no-console
      console.error('SpeechRecognition error', event);
      recognitionRef.current?.stop?.();
      setIsRecording(false);
      stopAudioAnalyser();
    };

    recognition.onend = () => {
      recognitionRef.current?.stop?.();
      setIsRecording(false);
      stopAudioAnalyser();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // ---------------- AUDIO ANALYSER ----------------
  const startAudioAnalyser = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const audioContext = new AudioContext({ latencyHint: 'interactive' });
    audioContextRef.current = audioContext;

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyserRef.current = analyser;

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    dataArrayRef.current = dataArray;

    const tick = () => {
      /* istanbul ignore next -- startAudioAnalyser initializes both refs before tick runs */
      if (!analyserRef.current || !dataArrayRef.current) return;

      analyserRef.current.getByteTimeDomainData(dataArrayRef.current as any);

      let sumSquares = 0;
      for (let i = 0; i < dataArrayRef.current.length; i++) {
        const v = (dataArrayRef.current[i] - 128) / 128;
        sumSquares += v * v;
      }

      const rms = Math.sqrt(sumSquares / dataArrayRef.current.length);

      let level = (rms * 1.8) ** 1.5;

      level = Math.min(1, level);

      setAudioLevel(level);
      animationFrameRef.current = requestAnimationFrame(tick);
    };

    tick();
  };

  const stopAudioAnalyser = () => {
    if (animationFrameRef.current)
      cancelAnimationFrame(animationFrameRef.current);

    streamRef.current?.getTracks().forEach((t) => t.stop());
    audioContextRef.current?.close();

    setAudioLevel(0);
  };

  // ---------------- PUBLIC API ----------------

  const toggleRecording = useCallback(
    async (cancel?: Boolean) => {
      if (recognitionRef?.current) {
        if (isRecording) {
          recognitionRef.current?.stop?.();
          setIsRecording(false);
          setTranscript(!cancel ? currentTranscript : '');
          setCurrentTranscript('');
          stopAudioAnalyser();
        } else {
          await startAudioAnalyser();
          recognitionRef.current?.start?.();
          setIsRecording(true);
        }
      }
    },
    [currentTranscript, isRecording],
  );

  return {
    isSpeechSupported,
    isRecording,
    transcript,
    audioLevel,
    toggleRecording,
  };
}
